import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import heic2any from "heic2any";
import type { TFunction } from "i18next";
import { isHeicLike } from "./convert-files-to-pdf";

/**
 * Converts any image Blob into PNG bytes via canvas.
 *
 * Why this exists?:
 * - pdf-lib can embed JPEG/PNG directly.
 * - For other image types (e.g. webp, bmp, gif, etc) we rasterize in-browser to PNG.
 */
async function imageFileToPngBytes(file: Blob, t: TFunction): Promise<Uint8Array> {
  const bitmap = await createImageBitmap(file);

  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error(t("pdfConversion.errors.canvasNotSupported"));

  ctx.drawImage(bitmap, 0, 0);

  const pngBlob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error(t("pdfConversion.errors.failedToConvertImageToPng")))),
      "image/png"
    );
  });

  return new Uint8Array(await pngBlob.arrayBuffer());
}

type EmbeddableImage = {
  bytes: Uint8Array;
  embedType: "jpg" | "png";
};

/**
 * Returns bytes in a format pdf-lib can embed (JPEG or PNG).
 *
 * - HEIC/HEIF: converted to JPEG via heic2any.
 * - JPEG/PNG: used as-is.
 * - Anything else: rasterize to PNG via canvas.
 */
async function getEmbeddableImage(file: File, t: TFunction): Promise<EmbeddableImage> {
  if (isHeicLike(file)) {
    const converted = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.92,
    });

    const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
    return { bytes: new Uint8Array(await jpegBlob.arrayBuffer()), embedType: "jpg" };
  }

  const fileType = (file.type || "").toLowerCase();

  // Fast paths for common formats (avoid canvas re-encoding).
  if (fileType === "image/jpeg" || fileType === "image/jpg") {
    return { bytes: new Uint8Array(await file.arrayBuffer()), embedType: "jpg" };
  }

  if (fileType === "image/png") {
    return { bytes: new Uint8Array(await file.arrayBuffer()), embedType: "png" };
  }

  // Fallback: convert other image types to PNG bytes we can embed.
  return { bytes: await imageFileToPngBytes(file, t), embedType: "png" };
}

/**
 * Converts an image-like file to a 1-page A4 PDF, centered with margins.
 */
export async function convertImageLikeToPdfBlob(file: File, t: TFunction): Promise<Blob> {
  const pdfDoc = await PDFDocument.create();
  const { bytes, embedType } = await getEmbeddableImage(file, t);

  const embedded = embedType === "jpg" ? await pdfDoc.embedJpg(bytes) : await pdfDoc.embedPng(bytes);

  // A4 page size in points (pdf-lib units).
  const pageWidth = 595.28;
  const pageHeight = 841.89;
  const margin = 24;

  // Scale to fit inside page margins while preserving aspect ratio.
  const maxW = pageWidth - margin * 2;
  const maxH = pageHeight - margin * 2;

  const scale = Math.min(maxW / embedded.width, maxH / embedded.height);
  const drawW = embedded.width * scale;
  const drawH = embedded.height * scale;

  const page = pdfDoc.addPage([pageWidth, pageHeight]);
  page.drawImage(embedded, {
    x: (pageWidth - drawW) / 2,
    y: (pageHeight - drawH) / 2,
    width: drawW,
    height: drawH,
  });

  const pdfBytes = await pdfDoc.save();
  return new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
}

/**
 * Fallback converter for non-image, non-PDF files.
 *
 * This does NOT "render" the original file. It produces a simple PDF containing:
 * - basic metadata (name/type/size)
 * - a note that the PDF was created in-browser
 *
 * If supported by the pdf-lib build, we also embed the original bytes as a PDF attachment.
 * (This is useful for preserving the original file, but we try to avoid using this path
 * for random/unsupported file types unless product explicitly wants it.)
 */
export async function convertNonImageToPdfBlob(file: File, t: TFunction): Promise<Blob> {
  const pdfDoc = await PDFDocument.create();

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const page = pdfDoc.addPage([595.28, 841.89]);
  const black = rgb(0, 0, 0);

  const title = t("pdfConversion.pdf.title");
  const lines = [
    t("pdfConversion.pdf.lines.originalFilename", { fileName: file.name }),
    t("pdfConversion.pdf.lines.originalMimeType", { mimeType: file.type || "unknown" }),
    t("pdfConversion.pdf.lines.originalSizeBytes", { size: file.size.toLocaleString() }),
    "",
    t("pdfConversion.pdf.lines.createdInBrowser"),
    t("pdfConversion.pdf.lines.officeRequiresBackend"),
  ];

  page.drawText(title, { x: 50, y: 780, size: 18, font, color: black });

  let y = 740;
  for (const line of lines) {
    page.drawText(line, { x: 50, y, size: 12, font, color: black });
    y -= 18;
  }

  // Optional: embed the original file as an attachment (if pdf-lib supports it in this build).
  const originalBytes = new Uint8Array(await file.arrayBuffer());
  const docAny = pdfDoc as any;

  const canAttach = typeof docAny.attach === "function";
  if (canAttach) {
    await docAny.attach(originalBytes, file.name, {
      mimeType: file.type || "application/octet-stream",
      description: t("pdfConversion.pdf.attachment.description"),
    });
  }

  page.drawText(
    canAttach ? t("pdfConversion.pdf.lines.attachmentEmbedded") : t("pdfConversion.pdf.lines.attachmentsNotSupported"),
    { x: 50, y: 560, size: 12, font, color: black }
  );

  const pdfBytes = await pdfDoc.save();
  return new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
}
